import type { BillingAccountResponse } from '../api-types/billing-account';
import {
  BILLING_API_BILLING_ACCOUNT_STATUS,
  BILLING_API_BILLING_MODEL,
  BILLING_API_BILLING_PROCESSOR,
  BILLING_API_PAYMENT_METHOD,
  BILLING_API_PRICING_PLAN_NAME,
} from '../api-types/billing-account';
import {
  BILLING_ACCOUNT_STATUS,
  BILLING_MODEL,
  BILLING_PROCESSOR,
  type BillingAccount,
  PAYMENT_METHOD,
  PRICING_PLAN_NAME,
} from '../types/billing-account';

const mapBillingModel = (value: BILLING_MODEL | BILLING_API_BILLING_MODEL): BILLING_MODEL => {
  switch (value) {
    case BILLING_API_BILLING_MODEL.PAY_AS_YOU_GO:
    case BILLING_MODEL.PAY_AS_YOU_GO:
      return BILLING_MODEL.PAY_AS_YOU_GO;
    case BILLING_API_BILLING_MODEL.PREPAID_CONSUMPTION:
    case BILLING_MODEL.PREPAID_CONSUMPTION:
      return BILLING_MODEL.PREPAID_CONSUMPTION;
    default:
      throw new Error('Invalid billing model');
  }
};

const mapBillingProcessor = (value: BILLING_PROCESSOR | BILLING_API_BILLING_PROCESSOR): BILLING_PROCESSOR => {
  switch (value) {
    case BILLING_API_BILLING_PROCESSOR.DIRECT:
    case BILLING_PROCESSOR.DIRECT:
      return BILLING_PROCESSOR.DIRECT;
    case BILLING_API_BILLING_PROCESSOR.AZURE_MARKETPLACE:
    case BILLING_PROCESSOR.AZURE_MARKETPLACE:
      return BILLING_PROCESSOR.AZURE_MARKETPLACE;
    case BILLING_API_BILLING_PROCESSOR.GCP_MARKETPLACE:
    case BILLING_PROCESSOR.GCP_MARKETPLACE:
      return BILLING_PROCESSOR.GCP_MARKETPLACE;
    case BILLING_API_BILLING_PROCESSOR.AWS_MARKETPLACE:
    case BILLING_PROCESSOR.AWS_MARKETPLACE:
      return BILLING_PROCESSOR.AWS_MARKETPLACE;
    default:
      throw new Error('Invalid billing processor');
  }
};

const mapPaymentMethod = (value: PAYMENT_METHOD | BILLING_API_PAYMENT_METHOD): PAYMENT_METHOD => {
  switch (value) {
    case BILLING_API_PAYMENT_METHOD.CREDIT_CARD:
    case PAYMENT_METHOD.CREDIT_CARD:
      return PAYMENT_METHOD.CREDIT_CARD;
    case BILLING_API_PAYMENT_METHOD.MARKETPLACE:
    case PAYMENT_METHOD.MARKETPLACE:
      return PAYMENT_METHOD.MARKETPLACE;
    case BILLING_API_PAYMENT_METHOD.BALANCE:
    case PAYMENT_METHOD.BALANCE:
      return PAYMENT_METHOD.BALANCE;
    default:
      throw new Error('Invalid payment method');
  }
};

const mapBillingAccountStatus = (
  value: BILLING_ACCOUNT_STATUS | BILLING_API_BILLING_ACCOUNT_STATUS,
): BILLING_ACCOUNT_STATUS => {
  switch (value) {
    case BILLING_API_BILLING_ACCOUNT_STATUS.CREATED:
    case BILLING_ACCOUNT_STATUS.CREATED:
      return BILLING_ACCOUNT_STATUS.CREATED;
    case BILLING_API_BILLING_ACCOUNT_STATUS.ACTIVATED:
    case BILLING_ACCOUNT_STATUS.ACTIVATED:
      return BILLING_ACCOUNT_STATUS.ACTIVATED;
    case BILLING_API_BILLING_ACCOUNT_STATUS.ACTIVE_IN_USE:
    case BILLING_ACCOUNT_STATUS.ACTIVE_IN_USE:
      return BILLING_ACCOUNT_STATUS.ACTIVE_IN_USE;
    case BILLING_API_BILLING_ACCOUNT_STATUS.SUSPENDED:
    case BILLING_ACCOUNT_STATUS.SUSPENDED:
      return BILLING_ACCOUNT_STATUS.SUSPENDED;
    case BILLING_API_BILLING_ACCOUNT_STATUS.DEACTIVATED:
    case BILLING_ACCOUNT_STATUS.DEACTIVATED:
      return BILLING_ACCOUNT_STATUS.DEACTIVATED;
    default:
      throw new Error('Invalid account status');
  }
};

const mapPricingPlan = (value: PRICING_PLAN_NAME | BILLING_API_PRICING_PLAN_NAME): PRICING_PLAN_NAME => {
  switch (value) {
    case BILLING_API_PRICING_PLAN_NAME.PAY_AS_YOU_GO:
    case PRICING_PLAN_NAME.PAY_AS_YOU_GO:
      return PRICING_PLAN_NAME.PAY_AS_YOU_GO;
    case BILLING_API_PRICING_PLAN_NAME.LEGACY_ENTERPRISE:
    case PRICING_PLAN_NAME.LEGACY_ENTERPRISE:
      return PRICING_PLAN_NAME.LEGACY_ENTERPRISE;
    case BILLING_API_PRICING_PLAN_NAME.VDC:
    case PRICING_PLAN_NAME.VIRTUAL_DEDICATED_CLOUD:
      return PRICING_PLAN_NAME.VIRTUAL_DEDICATED_CLOUD;
    case BILLING_API_PRICING_PLAN_NAME.NON_BILLABLE:
    case PRICING_PLAN_NAME.NON_BILLABLE:
      return PRICING_PLAN_NAME.NON_BILLABLE;
    case BILLING_API_PRICING_PLAN_NAME.NON_PAID:
    case PRICING_PLAN_NAME.NON_PAID:
      return PRICING_PLAN_NAME.NON_PAID;
    default:
      throw new Error('Invalid pricing plan');
  }
};

export const transformGetBillingAccountResponse = (res: BillingAccountResponse): BillingAccount => ({
  id: res.billing_account_id,
  name: res.name,
  billingModel: mapBillingModel(res.billing_model),
  billingProcessor: mapBillingProcessor(res.billing_processor),
  organizationId: res.organization_id,
  paymentMethod: mapPaymentMethod(res.payment_method),
  pricingPlan: mapPricingPlan(res.pricing_plan),
  projectIds: res.project_ids,
  email: res.email,
  reason: res.reason,
  status: mapBillingAccountStatus(res.status),
  isFunded: res.is_funded,
});
